import { Suspense, lazy } from "react";
import Index from "@/pages/Index";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AnalyticsProvider } from "@/hooks/useAnalytics";
import { ErrorBoundary } from "@/components/ErrorBoundary";


// Lazy load all page components for code splitting
const FailoverSolutions = lazy(() => import("./pages/FailoverSolutions"));
const FixedWirelessSolutions = lazy(() => import("./pages/FixedWirelessSolutions"));
const BusinessInternetSolutions = lazy(() => import("./pages/BusinessInternetSolutions"));
const IoTSolutions = lazy(() => import("./pages/IoTSolutions"));
const POTSReplacementSolutions = lazy(() => import("./pages/POTSReplacementSolutions"));
const StarlinkBusinessInternet = lazy(() => import("./pages/StarlinkBusinessInternet"));
const HomeInternet = lazy(() => import("./pages/HomeInternet"));
const About = lazy(() => import("./pages/About"));
const Resources = lazy(() => import("./pages/Resources"));
const ResourceDetail = lazy(() => import("./pages/ResourceDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));
const BusinessCore = lazy(() => import("./pages/BusinessCore"));
const BusinessAdvanced = lazy(() => import("./pages/BusinessAdvanced"));
const BusinessFailover = lazy(() => import("./pages/BusinessFailover"));
const BusinessUltimate = lazy(() => import("./pages/BusinessUltimate"));
const CoreFailover = lazy(() => import("./pages/CoreFailover"));
const AdvancedFailover = lazy(() => import("./pages/AdvancedFailover"));
const MissionCritical = lazy(() => import("./pages/MissionCritical"));
const EnterpriseShield = lazy(() => import("./pages/EnterpriseShield"));
const AssetTracking = lazy(() => import("./pages/AssetTracking"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const EndUserTerms = lazy(() => import("./pages/EndUserTerms"));
const LeasingAddendum = lazy(() => import("./pages/LeasingAddendum"));
const CameraAIAddendum = lazy(() => import("./pages/CameraAIAddendum"));
const AlarmFireAddendum = lazy(() => import("./pages/AlarmFireAddendum"));
const LLMText = lazy(() => import("./pages/LLMText"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminContacts = lazy(() => import("./pages/AdminContacts"));
const AdminResources = lazy(() => import("./pages/AdminResources"));
const AdminResourceEditor = lazy(() => import("./pages/AdminResourceEditor"));
const ContactDetail = lazy(() => import("./pages/ContactDetail"));
const AppointmentsCalendar = lazy(() => import("./pages/AppointmentsCalendar"));
const AppointmentsList = lazy(() => import("./pages/AppointmentsList"));
const AdminCalendar = lazy(() => import("./pages/AdminCalendar"));
const AppointmentDetail = lazy(() => import("./pages/AppointmentDetail"));
const Activities = lazy(() => import("./pages/Activities"));
const Reschedule = lazy(() => import("./pages/Reschedule"));
const BecomePartner = lazy(() => import("./pages/BecomePartner"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const UserApprovals = lazy(() => import("./pages/UserApprovals"));
const AdminQuotes = lazy(() => import("./pages/AdminQuotes"));
const AdminQuoteBuilder = lazy(() => import("./pages/AdminQuoteBuilder"));
const AdminQuoteDetail = lazy(() => import("./pages/AdminQuoteDetail"));
const AdminProducts = lazy(() => import("./pages/AdminProducts"));
const CustomerQuoteView = lazy(() => import("./pages/CustomerQuoteView"));
const AssetTrackingIntake = lazy(() => import("./pages/AssetTrackingIntake"));
const StarlinkNotAvailable = lazy(() => import("./pages/StarlinkNotAvailable"));
const InternetOutsideCityLimits = lazy(() => import("./pages/InternetOutsideCityLimits"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminChat = lazy(() => import("./pages/AdminChat"));
const GPSOrderForm = lazy(() => import("./pages/GPSOrderForm"));
const POTSOrderFormPage = lazy(() => import("./pages/POTSOrderForm"));
const PeplinkQuoteBuilder = lazy(() => import("./pages/PeplinkQuoteBuilder"));
const SignAgreement = lazy(() => import("./pages/SignAgreement"));
const AdminAgreements = lazy(() => import("./pages/AdminAgreements"));
const AdminTasks = lazy(() => import("./pages/AdminTasks"));
const BusinessCheckout = lazy(() => import("./pages/BusinessCheckout"));
const FieldComms = lazy(() => import("./pages/FieldComms"));
const FieldTabletsMobileInternet = lazy(() => import("./pages/FieldTabletsMobileInternet"));
const JobsiteConnectivity = lazy(() => import("./pages/JobsiteConnectivity"));
const PushToTalk = lazy(() => import("./pages/PushToTalk"));
const PortalLogin = lazy(() => import("./pages/portal/PortalLogin"));
const PortalDashboard = lazy(() => import("./pages/portal/PortalDashboard"));
const PortalOrganization = lazy(() => import("./pages/portal/PortalOrganization"));
const PortalLocations = lazy(() => import("./pages/portal/PortalLocations"));
const PortalLines = lazy(() => import("./pages/portal/PortalLines"));
const PortalTickets = lazy(() => import("./pages/portal/PortalTickets"));
const PortalUsers = lazy(() => import("./pages/portal/PortalUsers"));
const AdminResellerOrgs = lazy(() => import("./pages/AdminResellerOrgs"));
const AdminResellerOrgDetail = lazy(() => import("./pages/AdminResellerOrgDetail"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AnalyticsProvider>
          <ScrollToTop />
          <ErrorBoundary>
            <Suspense
              fallback={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "100vh",
                  }}
                >
                  Loading...
                </div>
              }
            >
              <Routes>
                <Route path="/" element={<FieldComms />} />
                <Route path="/all-solutions" element={<Index />} />
                <Route path="/field-communications" element={<Navigate to="/" replace />} />

                {/* 301 Redirects for old URLs */}
                <Route path="/all-technology-solutions" element={<Navigate to="/" replace />} />
                <Route path="/communications-solutions" element={<Navigate to="/" replace />} />
                <Route path="/software-as-a-service-saas" element={<Navigate to="/" replace />} />
                <Route path="/artificial-intelligence" element={<Navigate to="/" replace />} />
                <Route path="/managed-services" element={<Navigate to="/" replace />} />
                <Route path="/infrastructure-solutions" element={<Navigate to="/" replace />} />
                <Route path="/security-solutions" element={<Navigate to="/" replace />} />
                <Route path="/energy-solutions" element={<Navigate to="/" replace />} />
                <Route path="/hardware-and-logistics" element={<Navigate to="/" replace />} />

                <Route path="/failover-solutions" element={<FailoverSolutions />} />
                <Route path="/fixed-wireless-solutions" element={<FixedWirelessSolutions />} />
                <Route path="/business-internet-solutions" element={<BusinessInternetSolutions />} />
                <Route path="/internet-of-things-iot" element={<IoTSolutions />} />
                <Route path="/pots-replacement-solutions" element={<POTSReplacementSolutions />} />
                <Route path="/pots-replacement" element={<Navigate to="/pots-replacement-solutions" replace />} />
                <Route path="/starlink-business-internet" element={<StarlinkBusinessInternet />} />
                <Route path="/home-internet" element={<HomeInternet />} />
                <Route path="/about-us" element={<About />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/resources/:slug" element={<ResourceDetail />} />
                <Route path="/contact-us" element={<Contact />} />
                <Route path="/business-core" element={<BusinessCore />} />
                <Route path="/business-advanced" element={<BusinessAdvanced />} />
                <Route path="/business-failover" element={<BusinessFailover />} />
                <Route path="/business-ultimate" element={<BusinessUltimate />} />
                <Route path="/core-failover" element={<CoreFailover />} />
                <Route path="/advanced-failover" element={<AdvancedFailover />} />
                <Route path="/mission-critical" element={<MissionCritical />} />
                <Route path="/enterprise-shield" element={<EnterpriseShield />} />
                <Route path="/fleet-asset-tracking" element={<AssetTracking />} />
                <Route path="/asset-tracking-solutions" element={<Navigate to="/fleet-asset-tracking" replace />} />
                <Route path="/asset-tracking-intake" element={<AssetTrackingIntake />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/legal/end-user-terms" element={<EndUserTerms />} />
                <Route path="/legal/leasing-addendum" element={<LeasingAddendum />} />
                <Route path="/legal/camera-ai-addendum" element={<CameraAIAddendum />} />
                <Route path="/legal/alarm-fire-addendum" element={<AlarmFireAddendum />} />
                <Route path="/llm.txt" element={<LLMText />} />
                <Route path="/reschedule/:appointmentId" element={<Reschedule />} />
                <Route path="/become-a-partner" element={<BecomePartner />} />
                <Route path="/quotes/:id" element={<CustomerQuoteView />} />
                <Route path="/starlink-not-available" element={<StarlinkNotAvailable />} />
                <Route path="/internet-outside-city-limits" element={<InternetOutsideCityLimits />} />
                <Route path="/gps-order-form" element={<GPSOrderForm />} />
                <Route path="/pots-order-form" element={<POTSOrderFormPage />} />
                <Route path="/peplink-quote-builder" element={<PeplinkQuoteBuilder />} />
                <Route path="/sign/:token" element={<SignAgreement />} />
                <Route path="/get-connected" element={<BusinessCheckout />} />
                <Route path="/preview/new-homepage" element={<Navigate to="/" replace />} />
                <Route path="/field-tablets-mobile-internet" element={<FieldTabletsMobileInternet />} />
                <Route path="/jobsite-connectivity" element={<JobsiteConnectivity />} />
                <Route path="/push-to-talk" element={<PushToTalk />} />

                {/* Admin Login (Public) */}
                <Route path="/admin/login" element={<AdminLogin />} />

                {/* Protected Admin Routes */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/contacts"
                  element={
                    <ProtectedRoute>
                      <AdminContacts />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/contacts/:id"
                  element={
                    <ProtectedRoute>
                      <ContactDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/appointments"
                  element={
                    <ProtectedRoute>
                      <AppointmentsCalendar />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/appointments/list"
                  element={
                    <ProtectedRoute>
                      <AppointmentsList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/calendar"
                  element={
                    <ProtectedRoute>
                      <AdminCalendar />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/appointments/:id"
                  element={
                    <ProtectedRoute>
                      <AppointmentDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/activities"
                  element={
                    <ProtectedRoute>
                      <Activities />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/user-approvals"
                  element={
                    <ProtectedRoute>
                      <UserApprovals />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/user-management"
                  element={
                    <ProtectedRoute>
                      <UserManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/resources"
                  element={
                    <ProtectedRoute>
                      <AdminResources />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/resources/new"
                  element={
                    <ProtectedRoute>
                      <AdminResourceEditor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/resources/edit/:id"
                  element={
                    <ProtectedRoute>
                      <AdminResourceEditor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/quotes"
                  element={
                    <ProtectedRoute>
                      <AdminQuotes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/quotes/new"
                  element={
                    <ProtectedRoute>
                      <AdminQuoteBuilder />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/quotes/:id"
                  element={
                    <ProtectedRoute>
                      <AdminQuoteDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/quotes/:id/edit"
                  element={
                    <ProtectedRoute>
                      <AdminQuoteBuilder />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/products"
                  element={
                    <ProtectedRoute>
                      <AdminProducts />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/analytics"
                  element={
                    <ProtectedRoute>
                      <AdminAnalytics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/chat"
                  element={
                    <ProtectedRoute>
                      <AdminChat />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/tasks"
                  element={
                    <ProtectedRoute>
                      <AdminTasks />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/agreements"
                  element={
                    <ProtectedRoute>
                      <AdminAgreements />
                    </ProtectedRoute>
                  }
                />

                {/* Reseller Admin Pages */}
                <Route path="/admin/reseller-orgs" element={<ProtectedRoute><AdminResellerOrgs /></ProtectedRoute>} />
                <Route path="/admin/reseller-orgs/:id" element={<ProtectedRoute><AdminResellerOrgDetail /></ProtectedRoute>} />

                {/* POTS Reseller Portal (white-label) */}
                <Route path="/pots/portal" element={<Navigate to="/pots/portal/dashboard" replace />} />
                <Route path="/pots/portal/login" element={<PortalLogin />} />
                <Route path="/pots/portal/dashboard" element={<PortalDashboard />} />
                <Route path="/pots/portal/organization" element={<PortalOrganization />} />
                <Route path="/pots/portal/locations" element={<PortalLocations />} />
                <Route path="/pots/portal/lines" element={<PortalLines />} />
                <Route path="/pots/portal/tickets" element={<PortalTickets />} />
                <Route path="/pots/portal/users" element={<PortalUsers />} />

                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </AnalyticsProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;

